import api from './api';

export interface Order {
    _id: string;
    user?: {
        _id: string;
        name: string;
        email: string;
    };
    guestId?: string;
    items: {
        product: {
            _id: string;
            name: string;
            price: number;
            images: string[];
        };
        quantity: number;
        price: number;
    }[];
    deliveryAddress: {
        fullName: string;
        street: string;
        city: string;
        state: string;
        zipCode: string;
        phone: string;
    };
    paymentMethod: string;
    paymentStatus: string;
    subtotal: number;
    deliveryFee: number;
    tax: number;
    total: number;
    currency?: string;
    status: string;
    deliveryDate?: string;
    createdAt: string;
    updatedAt: string;
    specialInstructions?: string;
    notes?: {
        _id?: string;
        content: string;
        author: string;
        timestamp: string;
    }[];
    paymentDetails?: {
        proofUrl?: string;
        transactionId?: string;
    };
}

export interface OrdersResponse {
    orders: Order[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    filters?: any;
}

// S43B: Enhanced Filters Interface
export interface OrderQueryParams {
    page?: number;
    limit?: number;
    status?: string;
    paymentStatus?: string;
    paymentMethod?: string;
    region?: string;
    customerId?: string;
    startDate?: string;
    endDate?: string;
    minTotal?: number;
    maxTotal?: number;
    search?: string;
    sort?: string;
}

// S43B: Enhanced getOrders
export const getOrders = async (
    pageOrParams: number | OrderQueryParams = 1,
    limit: number = 10,
    status: string = 'all',
    paymentStatus: string = 'all'
) => {
    let params: any = {};

    if (typeof pageOrParams === 'number') {
        // Legacy Support
        params = { page: pageOrParams, limit, status, paymentStatus };
    } else {
        // New Deep-Dive Support
        params = { ...pageOrParams };
    }

    // Capture the standard response
    const response = await api.get<any>(`/orders`, { params });

    // SOVEREIGN UNWRAP (MANDATE-S389-DEV)
    // Check if the response matches the JSend envelope structure (data.data)
    if (response.data && response.data.status === 'success' && response.data.data) {
        return response.data.data; // 🟢 Return Inner Data
    }

    return response.data; // 🟡 Return Raw if not enveloped
};

export const getOrderById = async (id: string) => {
    const response = await api.get<Order>(`/orders/${id}`);
    return response.data;
};

export const updateOrderStatus = async (id: string, status?: string, paymentStatus?: string, deliveryDate?: string) => {
    const response = await api.patch(`/orders/${id}/status`, { status, paymentStatus, deliveryDate });
    return response.data;
};

// WO-807: Driver Actions
export const getMyActiveDeliveries = async () => {
    const response = await api.get('/orders/my-active-deliveries');
    return response.data;
};

export const getMyJob = async (id: string) => {
    const response = await api.get(`/orders/my-active-deliveries/${id}`);
    return response.data;
};

export const completeOrder = async (id: string) => {
    const response = await api.post(`/orders/${id}/complete`);
    return response.data;
};

export const failOrder = async (id: string, reason: string) => {
    const response = await api.post(`/orders/${id}/fail`, { reason });
    return response.data;
};

export const verifyPayment = async (id: string) => {
    const response = await api.post(`/orders/${id}/verify-payment`);
    return response.data;
};

export const rejectPayment = async (id: string, reason?: string) => {
    const response = await api.post(`/orders/${id}/reject-payment`, { reason });
    return response.data;
};

export const addOrderNote = async (id: string, content: string) => {
    const response = await api.post<Order>(`/orders/${id}/notes`, { content });
    return response.data;
};

export const deleteOrder = async (id: string) => {
    const response = await api.delete(`/orders/${id}`);
    return response.data;
};

// S43B: Bulk Actions
export const bulkUpdateOrders = async (orderIds: string[], action: string) => {
    // Placeholder for Phase 2 Implementation
    // const response = await api.post('/orders/bulk', { orderIds, action });
    // return response.data;
    console.warn('Bulk Update API not implemented yet');
    return { success: true };
};

export const exportOrdersCSV = async (params: OrderQueryParams) => {
    // Placeholder for Phase 2 Implementation
    console.warn('Export API not implemented yet');
    return;
};
